import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/PageShell";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";
import { formatRp } from "@/lib/tiers";
import { Sparkles, Lock, Crown, Flame, ArrowRight, BookKey, Trophy, Users } from "lucide-react";
import PostCard from "@/components/PostCard";
import RoleBadge from "@/components/RoleBadge";
import Avatar from "@/components/Avatar";
import FounderNote from "@/components/FounderNote";
import VotingWidget from "@/components/VotingWidget";
import TranslationProgressTracker from "@/components/TranslationProgressTracker";
import FeaturedHeroCarousel from "@/components/FeaturedHeroCarousel";

export default function HomePage() {
  const { settings } = useSettings();
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [stats, setStats] = useState({ total_members: 0, god_tier_count: 0, top_supporters: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: feat } = await api.get("/posts", { params: { featured: true, limit: 5 } });
        if (!cancelled) setFeatured(feat.posts || []);
        const { data: lat } = await api.get("/posts", { params: { limit: 8 } });
        if (!cancelled) setLatest(lat.posts || []);
        const { data: st } = await api.get("/stats/home");
        if (!cancelled) setStats(st);
      } catch (e) {
        console.error("Failed to load homepage data:", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0205] to-black" />
          <div className="absolute -top-40 right-[-10%] w-[600px] h-[600px] rounded-full bg-rose-700/20 blur-[120px]" />
          <div className="absolute top-40 left-[-10%] w-[500px] h-[500px] rounded-full bg-violet-700/15 blur-[120px]" />
        </div>

        <div className="absolute top-32 right-10 hidden lg:block opacity-[0.07] spin-slow pointer-events-none">
          <div className="font-mystic text-[220px] leading-none text-rose-400">鏡花黎明</div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-20 pb-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-xs text-zinc-300 tracking-widest font-mystic">Welcome, traveler</span>
            </div>
            <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Unlock the <span className="text-rose-500 drop-shadow-[0_0_18px_rgba(225,29,72,0.55)]">Dawn</span><br/>
              of Translated <span className="font-mystic font-light text-rose-200/90 italic">Worlds</span>
            </h1>
            <p className="mt-6 font-mystic italic text-zinc-300 text-lg max-w-xl">
              {settings.tagline}. Join the cult, support the scribes, and unseal forbidden visual novels & Japanese games—translated with reverence.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/membership" data-testid="hero-cta-membership">
                <Button className="bg-rose-600 hover:bg-rose-500 text-white px-7 py-6 rounded-md font-semibold tracking-wide shadow-[0_0_30px_rgba(225,29,72,0.45)]">
                  <Crown className="w-4 h-4 mr-2" /> Join the Cult
                </Button>
              </Link>
              <Link to="/posts" data-testid="hero-cta-browse">
                <Button variant="outline" className="bg-white/5 hover:bg-white/10 text-white border-white/15 px-7 py-6 rounded-md font-semibold">
                  Browse TL Games <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>

          <FeaturedHeroCarousel featured={featured} />
        </div>
      </section>

      {/* Community Section: Founder Note & Exclusive Polling */}
      <div className="max-w-7xl mx-auto px-6 pt-10">
        <FounderNote />
        <TranslationProgressTracker />
        <VotingWidget />
      </div>

      {/* Latest TL Games */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mystic text-rose-400 text-sm">最新の翻訳</div>
            <h2 className="font-display text-3xl font-bold text-white tracking-tight">Latest TL Releases</h2>
          </div>
          <Link to="/posts" className="text-sm text-zinc-400 hover:text-rose-300 inline-flex items-center" data-testid="latest-view-all">
            View all <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        {latest.length === 0 ? (
          <div className="text-center py-20 text-zinc-500 font-mystic text-lg">No TL games have been unsealed yet…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {latest.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        )}
      </section>

      {/* Community Stats — Top Supporters + Total Members */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="font-mystic text-rose-400 text-sm">信者の集い</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">The Cult is Growing</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="p-6 rounded-xl border border-white/10 bg-zinc-950 text-center" data-testid="home-stat-members">
            <Users className="w-8 h-8 mx-auto text-rose-400" />
            <div className="font-display text-4xl font-black text-white mt-3">{stats.total_members.toLocaleString()}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Members Joined</div>
          </div>
          <div className="p-6 rounded-xl border border-amber-500/30 bg-zinc-950 text-center shadow-[0_0_24px_rgba(251,191,36,0.12)]" data-testid="home-stat-god-tier">
            <Sparkles className="w-8 h-8 mx-auto text-amber-300" />
            <div className="font-display text-4xl font-black text-amber-200 mt-3">{stats.god_tier_count.toLocaleString()}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">God Tier Supporters</div>
          </div>
          <div className="p-6 rounded-xl border border-rose-500/30 bg-zinc-950 text-center shadow-[0_0_24px_rgba(225,29,72,0.14)]" data-testid="home-stat-top">
            <Trophy className="w-8 h-8 mx-auto text-rose-300" />
            <div className="font-display text-4xl font-black text-white mt-3">Top 3</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">All-Time Supporters</div>
          </div>
        </div>

        {stats.top_supporters && stats.top_supporters.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="home-top-supporters">
            {stats.top_supporters.slice(0, 3).map((s, idx) => (
              <Link
                key={s.user_id}
                to="/leaderboard"
                className={`relative p-5 rounded-xl border bg-zinc-950 transition hover:scale-[1.02] ${
                  idx === 0 ? "border-amber-400/50 shadow-[0_0_24px_rgba(251,191,36,0.18)]"
                  : idx === 1 ? "border-zinc-300/30 shadow-[0_0_20px_rgba(228,228,231,0.10)]"
                  : "border-orange-500/30 shadow-[0_0_20px_rgba(234,88,12,0.12)]"
                }`}
                data-testid={`home-top-supporter-${idx + 1}`}
              >
                <div className={`absolute -top-3 left-5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                  idx === 0 ? "bg-amber-500 text-amber-950"
                  : idx === 1 ? "bg-zinc-300 text-zinc-900"
                  : "bg-orange-600 text-orange-50"
                }`}>
                  Rank #{idx + 1}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <Avatar user={s} size={48} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-white font-bold truncate">{s.name}</div>
                    <RoleBadge role={s.role} badges={s.badges} size="xs" />
                  </div>
                </div>
                <div className="mt-3 text-amber-300 font-display font-bold text-lg">
                  {formatRp(s.total_contribution)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Tiers teaser */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="font-mystic text-rose-400 text-sm">入信</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">Three Ranks in the Cult</h2>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Choose your devotion. Higher ranks unseal more forbidden translations.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <TierTeaser icon={<BookKey className="w-5 h-5"/>} title="Member" desc="Free access to public releases and community posts." price="Free" theme="bg-zinc-900 border-white/10" />
          <TierTeaser icon={<Crown className="w-5 h-5"/>} title="Elite Cultist" desc="Unseal Elite-only translations and early drops." price={`${formatRp(settings.elite_price)} / 30 days`} theme="border-violet-500/40 bg-violet-950/30 glow-violet" />
          <TierTeaser icon={<Flame className="w-5 h-5"/>} title="Hardcore Cultist" desc="The full grimoire — every release, every patch." price={`${formatRp(settings.hardcore_price)} / 30 days`} theme="border-rose-500/40 bg-rose-950/30 glow-crimson" featured />
        </div>
        <div className="mt-10 text-center">
          <Link to="/membership" data-testid="tiers-cta">
            <Button className="bg-rose-600 hover:bg-rose-500 px-8 py-6 font-semibold">Become a Cultist <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>
      </section>
    </PageShell>
  );
}

function TierTeaser({ icon, title, desc, price, theme, featured }) {
  return (
    <div className={`relative p-8 rounded-xl border ${theme} transition`}>
      {featured && <div className="absolute -top-3 left-6 px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-semibold tracking-wider">MOST POPULAR</div>}
      <div className="flex items-center gap-2 text-rose-300">{icon}<span className="text-xs uppercase tracking-widest">Tier</span></div>
      <h3 className="font-display text-2xl font-bold text-white mt-3">{title}</h3>
      <p className="text-zinc-400 text-sm mt-2">{desc}</p>
      <div className="mt-6 font-display text-lg font-semibold text-white">{price}</div>
    </div>
  );
}